import type { SeoPage } from "@/lib/utils/seo";

/**
 * Trade events VARS exhibits at.
 *
 * Only events VARS has actually participated in or is committed to belong
 * here. Two entries that used to sit on the About page — Seafood Expo Global
 * in Barcelona and an "Aqua Middle East Expo" in Dubai — were not real
 * participations and have been removed, along with their copy in all nine
 * locales. A fabricated exhibitor listing is the same class of claim as a
 * certification nobody holds: a buyer can check it, and checking is the point.
 *
 * Display strings are ids resolved through `t("events.<id>.<field>")`, the
 * convention TURKEY_LOCATIONS uses. City, country, venue and stand are proper
 * nouns and identifiers, so they stay here.
 */

export interface TradeEvent {
  id: string;
  /** URL segment: /events/<slug>. */
  slug: string;
  /** ISO dates. Drive ordering, Event JSON-LD, and upcoming/past state. */
  startDate: string;
  endDate: string;
  city: string;
  country: string;
  venue?: string;
  hall?: string;
  /** Stand number, when VARS has one confirmed. */
  stand?: string;
  /** VARS's verified relationship to the event. */
  participation: "exhibitor" | "attendee";
  /** Organiser's site. */
  externalHref?: string;
  /** Official organizer-hosted proof of VARS's participation, when available. */
  participationHref?: string;
  /** Set when the event has hand-written nine-locale metadata. */
  seoPage?: SeoPage;
  /** Only Future Fish has a transcribed floor plan. */
  hasHallMap?: boolean;
}

export const TRADE_EVENTS: TradeEvent[] = [
  {
    id: "future-fish-eurasia-2026",
    slug: "future-fish-eurasia-2026",
    startDate: "2026-09-24",
    endDate: "2026-09-26",
    city: "İzmir",
    country: "Türkiye",
    venue: "Fuar İzmir",
    hall: "C",
    stand: "C-237",
    participation: "exhibitor",
    externalHref: "https://eurasiafairs.com/",
    seoPage: "expoFutureFish",
    hasHallMap: true,
  },
  {
    id: "bisfe",
    slug: "bisfe-busan",
    startDate: "2024-11-06",
    endDate: "2024-11-08",
    city: "Busan",
    country: "South Korea",
    venue: "BEXCO Exhibition Center I",
    hall: "1–3A",
    participation: "exhibitor",
    externalHref: "https://www.bisfe.com/",
    participationHref:
      "https://bisfe.com/board/bbs/board.php?bo_table=exhibitor_eng&wr_id=9&sca=2024&sfl=wr_subject&stx=vars&sop=and",
  },
  // Attended, not exhibited — no stand or hall, and the event page's JSON-LD
  // must not claim a performer role for VARS on this one.
  {
    id: "ontario-aquaculture-2025",
    slug: "ontario-aquaculture-conference-2025",
    startDate: "2025-02-26",
    endDate: "2025-02-27",
    city: "Kawartha Lakes",
    country: "Canada",
    venue: "Fleming College — Frost Campus",
    participation: "attendee",
    externalHref: "https://ontarioseafoodfarmers.ca/ontario-aquaculture-conference-2025/",
  },
];

export const eventPath = (event: TradeEvent) => `/events/${event.slug}` as const;

/** Soonest first. ISO dates, so lexical order is chronological. */
export const eventsByDate = () =>
  [...TRADE_EVENTS].sort((a, b) => a.startDate.localeCompare(b.startDate));

export const getEvent = (id: string) => TRADE_EVENTS.find((e) => e.id === id);
export const getEventBySlug = (slug: string) => TRADE_EVENTS.find((e) => e.slug === slug);

/** Everything except the given event, soonest first. */
export const otherEvents = (id: string) => eventsByDate().filter((e) => e.id !== id);
